import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { RowMenu } from '../ui/RowMenu';
import { DeleteDialog, GeneralError, issueFor, PACKAGES_RESOURCE, PRICING_KEY, PricingNav } from './PricingLayout';
import { NotesInput } from '../ui/NotesInput';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

/** As the API sends it; `priceDayGrosze` is absent for a role that may not see prices. */
export type Package = { id: string; name: string; brandLabel: string; priceDayGrosze?: number; blockId?: string | null; blockName?: string | null; notes: string; version: number };
type BlockSummary = { id: string; name: string };

const FIELDS = ['name', 'brandLabel', 'priceDayGrosze', 'blockId', 'notes'] as const;
const BLOCKS_RESOURCE = 'templates.blocks';

const draftOf = (p?: Package) => ({
  name: p?.name ?? '',
  brandLabel: p?.brandLabel ?? '',
  price: p?.priceDayGrosze !== undefined ? plnToInput(p.priceDayGrosze) : '',
  blockId: p?.blockId ?? '',
  notes: p?.notes ?? '',
});

/** Add (no `pkg`) or edit. Only visible fields are shown and sent; on edit only the changed ones (rule 7a). */
function PackageForm({ pkg, showPrice, showBlock, blocks, onClose }: { pkg?: Package; showPrice: boolean; showBlock: boolean; blocks: BlockSummary[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(pkg);
  const [draft, setDraft] = useState(draftOf(pkg));
  const [priceError, setPriceError] = useState<string | null>(null);
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });

  const save = useMutation({
    mutationFn: (priceDayGrosze: number | null) => {
      const original = draftOf(base);
      const changed = (key: keyof typeof draft) => !base || draft[key] !== original[key];
      const fields = {
        ...(changed('name') ? { name: draft.name } : {}),
        ...(changed('brandLabel') ? { brandLabel: draft.brandLabel } : {}),
        ...(showPrice && priceDayGrosze !== null && (!base || priceDayGrosze !== base.priceDayGrosze) ? { priceDayGrosze } : {}),
        ...(showBlock && changed('blockId') && (base || draft.blockId !== '') ? { blockId: draft.blockId === '' ? null : draft.blockId } : {}),
        ...(changed('notes') && (base || draft.notes !== '') ? { notes: draft.notes } : {}),
      };
      return base ? api<Package>(`/api/pricing/packages/${base.id}`, { method: 'PATCH', body: { version: base.version, ...fields } }) : api<Package>('/api/pricing/packages', { method: 'POST', body: fields });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      onClose();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  function reload() {
    const current = (save.error as ApiError).current as Package | undefined;
    if (current) {
      setBase(current);
      setDraft(draftOf(current));
    }
    save.reset();
    void queryClient.invalidateQueries({ queryKey: PRICING_KEY });
  }

  /** The price to send (null: not shown), or undefined with the reason shown. */
  function price(): number | null | undefined {
    let p: number | null = null;
    if (showPrice) {
      try {
        p = draft.price.trim() === '' ? 0 : parsePln(draft.price);
      } catch (err) {
        setPriceError((err as Error).message);
        return undefined;
      }
    }
    setPriceError(null);
    return p;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const p = price();
    if (p !== undefined) save.mutate(p);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(draft, draftOf(base)),
    save: async (el) => {
      const p = price();
      if (p === undefined || !validForm(el)) return false;
      await save.mutateAsync(p);
      return true;
    },
  });

  const err = (path: string) => issueFor(save.error, path);
  return (
    <form ref={guardRef} className="panel form" aria-label={base ? `Edycja: ${base.name}` : 'Nowy pakiet'} data-feature-id="pricing.packages" onSubmit={submit}>
      {!base && <h2>Nowy pakiet</h2>}
      {conflict && <ConflictNotice onReload={reload} />}
      <GeneralError error={save.error} fields={FIELDS} />
      <label className="field">
        <span>Nazwa</span>
        <input value={draft.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" placeholder="np. Zestaw na zespół" />
        {err('name') && <span className="field-error">{err('name')}</span>}
      </label>
      <label className="field">
        <span>Marka (w kosztorysie i PDF)</span>
        <input value={draft.brandLabel} onChange={(e) => set({ brandLabel: e.target.value })} autoComplete="off" placeholder="puste = nazwa pakietu" />
        {err('brandLabel') && <span className="field-error">{err('brandLabel')}</span>}
      </label>
      {showPrice && (
        <label className="field">
          <span>Cena za dobę (zł)</span>
          <NumberInput kind="money" value={draft.price} onValueChange={(v) => set({ price: v })} placeholder="np. 1 200,00" />
          {(priceError ?? err('priceDayGrosze')) && <span className="field-error">{priceError ?? err('priceDayGrosze')}</span>}
        </label>
      )}
      {showBlock && (
        <label className="field">
          <span>Skład w sprzętówce (blok sprzętu)</span>
          <select value={draft.blockId} onChange={(e) => set({ blockId: e.target.value })}>
            <option value="">— bez bloku —</option>
            {draft.blockId !== '' && !blocks.some((b) => b.id === draft.blockId) && <option value={draft.blockId}>{base?.blockName ?? '(blok niewidoczny)'}</option>}
            {blocks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <span className="hint">„Utwórz sprzętówkę z kosztorysu” wstawi sprzęt tego bloku. Bez bloku pakiet jest tylko ceną.</span>
          {err('blockId') && <span className="field-error">{err('blockId')}</span>}
        </label>
      )}
      <label className="field">
        <span>Uwagi</span>
        <NotesInput value={draft.notes} onChange={(e) => set({ notes: e.target.value })} />
        {err('notes') && <span className="field-error">{err('notes')}</span>}
      </label>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj pakiet'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/** "Cenniki → Pakiety" (F26): one price per day for a set, optionally made of a block of equipment. */
export function PackagesPage() {
  const { me } = useMe();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Package | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const list = useQuery({ queryKey: [...PRICING_KEY, 'packages'], queryFn: () => api<{ packages: Package[] }>('/api/pricing/packages') });
  const seeBlocks = !!me && can(me, BLOCKS_RESOURCE, 'view');
  const blocksQ = useQuery({ queryKey: ['templates', 'blocks', 'for-packages'], enabled: seeBlocks, queryFn: () => api<{ blocks: BlockSummary[] }>('/api/templates/blocks') });
  if (!me) return null;
  const packages = list.data?.packages ?? [];
  const showPrice = fieldVisible(me, PACKAGES_RESOURCE, 'priceDayGrosze');
  const showBlock = fieldVisible(me, PACKAGES_RESOURCE, 'blockId') && seeBlocks;
  const seeHistory = can(me, 'history.entries', 'view');
  const blocks = blocksQ.data?.blocks ?? [];
  const columns = 3 + (showPrice ? 1 : 0);

  return (
    <section className="page" data-feature-id="pricing.packages">
      <div className="page-head">
        <h2>Cenniki</h2>
        {can(me, PACKAGES_RESOURCE, 'create') && !creating && editingId === null && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => setCreating(true))}>
            Dodaj pakiet
          </button>
        )}
      </div>
      <PricingNav me={me} />
      <p className="muted">Pakiet to jedna cena za dobę za cały zestaw, np. „Zestaw na zespół” do wycen podstawowych wydarzeń. W kosztorysie dodajesz go szybkim wybieraniem jak sprzęt.</p>

      {creating && <PackageForm showPrice={showPrice} showBlock={showBlock} blocks={blocks} onClose={() => setCreating(false)} />}

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać pakietów'}</p>}
      {list.data && packages.length === 0 && !creating && <p className="muted">Brak pakietów. Dodaj pierwszy przyciskiem „Dodaj pakiet”.</p>}
      {packages.length > 0 && (
        <div className="table-wrap">
          <table className="table pricing-table" aria-label="Pakiety">
            <thead>
              <tr>
                <th scope="col">Nazwa</th>
                {showPrice && (
                  <th scope="col" className="num">
                    Cena za dobę
                  </th>
                )}
                <th scope="col" className="hide-phone">
                  Skład (blok)
                </th>
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => {
                const more = [
                  ...(seeHistory ? [{ label: historyId === p.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === p.id ? null : p.id) }] : []),
                  ...(can(me, PACKAGES_RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(p) }] : []),
                ];
                return (
                  <Fragment key={p.id}>
                    <tr>
                      <td>
                        <strong>{p.name}</strong>
                        {p.brandLabel && <span className="muted"> · {p.brandLabel}</span>}
                      </td>
                      {showPrice && <td className="num">{p.priceDayGrosze !== undefined ? formatPln(p.priceDayGrosze) : ''}</td>}
                      <td className="hide-phone">{p.blockName ?? '—'}</td>
                      <td className="tpl-actions">
                        {can(me, PACKAGES_RESOURCE, 'edit') && editingId !== p.id && (
                          <button className="btn small" type="button" aria-label={`Edytuj: ${p.name}`} onClick={() => leaveGuarded(() => setEditingId(p.id))}>
                            Edytuj
                          </button>
                        )}
                        <RowMenu label={`Więcej działań: ${p.name}`} actions={more} />
                      </td>
                    </tr>
                    {editingId === p.id && (
                      <tr className="row-editor">
                        <td colSpan={columns}>
                          <PackageForm pkg={p} showPrice={showPrice} showBlock={showBlock} blocks={blocks} onClose={() => setEditingId(null)} />
                        </td>
                      </tr>
                    )}
                    {historyId === p.id && seeHistory && (
                      <tr>
                        <td colSpan={columns}>
                          <HistoryPanel entityId={p.id} resource={PACKAGES_RESOURCE} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {deleting && <DeleteDialog title="Usunąć pakiet?" name={deleting.name} url={`/api/pricing/packages/${deleting.id}?version=${deleting.version}`} onClose={() => setDeleting(null)} />}
    </section>
  );
}
